import { useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import api from '../../utils/api';
import Badge from '../../components/Badge';
import DepartmentSelect from '../../components/DepartmentSelect';
import { formatDate, getCertStatus } from '../../utils/dateHelpers';
import { exportToExcel } from '../../utils/exportUtils';
const CERT_TYPES = ['BLS', 'ACLS', 'ATLS', 'NRP'];

export default function ExpiryTracker() {
  const [nearExpiry, setNearExpiry] = useState([]);
  const [expired, setExpired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('near-expiry');
  const [filterDept, setFilterDept] = useState('');
  const [filterCert, setFilterCert] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [ne, ex] = await Promise.all([
          api.get('/staff/near-expiry'),
          api.get('/staff/expired'),
        ]);
        setNearExpiry(ne.staff || []);
        setExpired(ex.staff || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const data = tab === 'near-expiry' ? nearExpiry : expired;
  const certKey = tab === 'near-expiry' ? 'near_expiry_certs' : 'expired_certs';

  const filtered = data.filter((s) => {
    if (filterDept && s.department !== filterDept) return false;
    if (filterCert && !(s[certKey] || []).includes(filterCert)) return false;
    return true;
  });

  // Group by department
  const grouped = {};
  filtered.forEach((s) => {
    const dept = s.department || 'Unassigned';
    if (!grouped[dept]) grouped[dept] = [];
    grouped[dept].push(s);
  });

  const handleExport = () => {
    const rows = filtered.map((s) => ({
      'Employee ID': s.employee_id,
      'Name': s.staff_name,
      'Department': s.department,
      'Position': s.position,
      'Flagged Certs': (s[certKey] || []).join(', '),
      'BLS Expiry': s.bls_expiry_date,
      'ACLS Expiry': s.acls_expiry_date,
      'ATLS Expiry': s.atls_expiry_date,
      'NRP Expiry': s.nrp_expiry_date,
    }));
    exportToExcel(rows, tab === 'near-expiry' ? 'near_expiry_tracker' : 'expired_tracker');
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className={`btn btn-sm ${tab === 'near-expiry' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('near-expiry')}>
              Near Expiry ({nearExpiry.length})
            </button>
            <button className={`btn btn-sm ${tab === 'expired' ? 'btn-primary' : 'btn-secondary'}`} style={tab === 'expired' ? { backgroundColor: '#C0392B', borderColor: '#C0392B' } : {}} onClick={() => setTab('expired')}>
              Expired ({expired.length})
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <DepartmentSelect value={filterDept} onChange={setFilterDept} placeholder="All Departments" style={{ width: '180px' }} />
            <select value={filterCert} onChange={(e) => setFilterCert(e.target.value)} style={{ width: '140px' }}>
              <option value="">All Certs</option>
              {CERT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button className="btn btn-sm btn-secondary" onClick={handleExport}>
              <FiDownload size={14} /> Export
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card text-center p-4"><div className="spinner" style={{ margin: '40px auto' }} /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card text-center p-4">
          <p className="text-muted">No {tab === 'near-expiry' ? 'near-expiry' : 'expired'} certificates found.</p>
        </div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([dept, members]) => (
          <div key={dept} className="card" style={{ marginBottom: '16px' }}>
            <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {dept}
              <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#6b6b6b' }}>({members.length} staff)</span>
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Employee ID</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Position</th>
                    <th style={thStyle}>Flagged</th>
                    <th style={thStyle}>BLS Expiry</th>
                    <th style={thStyle}>ACLS Expiry</th>
                    <th style={thStyle}>ATLS Expiry</th>
                    <th style={thStyle}>NRP Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((s) => (
                    <tr key={s.employee_id}>
                      <td style={tdStyle}>{s.employee_id}</td>
                      <td style={tdStyle}>{s.staff_name}</td>
                      <td style={tdStyle}>{s.position}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {(s[certKey] || []).map((c) => <Badge key={c} status={tab === 'near-expiry' ? 'expiring' : 'expired'} text={c} />)}
                        </div>
                      </td>
                      <td style={tdStyle}>{s.bls_expiry_date ? <ExpiryCell date={s.bls_expiry_date} /> : '—'}</td>
                      <td style={tdStyle}>{s.acls_expiry_date ? <ExpiryCell date={s.acls_expiry_date} /> : '—'}</td>
                      <td style={tdStyle}>{s.atls_expiry_date ? <ExpiryCell date={s.atls_expiry_date} /> : '—'}</td>
                      <td style={tdStyle}>{s.nrp_expiry_date ? <ExpiryCell date={s.nrp_expiry_date} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ExpiryCell({ date }) {
  const status = getCertStatus(date);
  return (
    <div>
      <div style={{ fontSize: '0.85rem' }}>{formatDate(date)}</div>
      <Badge status={status} text={status} />
    </div>
  );
}

const thStyle = { padding: '10px 12px', fontSize: '0.8rem', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #f0ece4', color: '#2B362E', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', fontSize: '0.85rem', borderBottom: '1px solid #f0ece4' };
